/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_DANGER,
  BUTTON_THEME_DEFAULT,
  Button,
  DialogContentProps,
  TOAST_TYPE_SUCCESS,
  Toast,
  ToastProps,
} from '@squiz/sds';
import { getFileStoreMappingsService } from 'apps/digital-asset-management/src/Services/file-store-mappings';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import { STATIC_SITES_DATA_IDS } from '../../StaticSites.constants';
import { DeleteRouteDialogContentProps } from './DeleteRouteDialogContent.props';

export const DeleteRouteDialogContent = (props: DialogContentProps): JSX.Element => {
  const { t } = useTranslation();

  const { close, setAfterClose, setButtons, setButtonKeys } = props;
  const { clearCache, id } = props.customProps as DeleteRouteDialogContentProps;

  // Services
  const fileStoreMappingsService = getFileStoreMappingsService();
  const { updateActiveToast } = useToastStore();

  // State
  const [isFormSubmitted, setSubmit] = useState<boolean>(false);

  // Labels
  const labels = useMemo(() => {
    return {
      cancel: t(CONFIGURATION_STRINGS.cancel),
      delete: t(CONFIGURATION_STRINGS.delete),
      deleteRouteDialogText: t(CONFIGURATION_STRINGS.deleteRouteDialogText),
      error: t(CONFIGURATION_STRINGS.error),
      errorUnknown: t(CONFIGURATION_STRINGS.errorUnknown),
      routeDeleted: t(CONFIGURATION_STRINGS.routeDeleted),
    };
  }, [t]);

  const customClose = useCallback(() => {
    // Refresh mapping list on dialog close
    clearCache(new Date().toISOString());
  }, [clearCache]);

  const deleteRoute = useCallback(async () => {
    const successToastProps: ToastProps = {
      showToast: true,
      stateHandler: useToastStore,
      title: labels.routeDeleted,
      toastType: TOAST_TYPE_SUCCESS,
    };

    const errorToastProps: ToastProps = {
      showToast: true,
      stateHandler: useToastStore,
      title: labels.error,
      toastContent: <>{labels.errorUnknown}</>,
      toastType: TOAST_TYPE_SUCCESS,
    };

    lastValueFrom(fileStoreMappingsService.deleteMapping({ id }))
      .then(() => {
        updateActiveToast(<Toast {...successToastProps} data-id={STATIC_SITES_DATA_IDS.deleteToastSuccess} />);
        return close();
      })
      .catch((err) => {
        updateActiveToast(<Toast {...errorToastProps} data-id={STATIC_SITES_DATA_IDS.deleteToastFailure} />);
        return close();
      });
  }, [close, fileStoreMappingsService, id, labels, updateActiveToast]);

  useEffect(() => {
    const cancelButton = (
      <Button
        classes="delete-route__btn--cancel"
        dataId={STATIC_SITES_DATA_IDS.actionDeleteRouteCancel}
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );

    const deleteButton = (
      <Button
        classes="delete-route__btn--delete"
        dataId={STATIC_SITES_DATA_IDS.actionDeleteRouteConfirm}
        format={BUTTON_FORMAT_STRONG}
        isLoading={isFormSubmitted}
        onClick={async (): Promise<void> => setSubmit(true)}
        text={labels.delete}
        theme={BUTTON_THEME_DANGER}
      />
    );

    setAfterClose && setAfterClose(() => customClose);

    // Form submission check
    if (isFormSubmitted) {
      setButtonKeys([`delete`]);
      setButtons([deleteButton]);
      deleteRoute();
    } else {
      setButtonKeys([`cancel`, `delete`]);
      setButtons([cancelButton, deleteButton]);
    }
  }, [close, customClose, deleteRoute, isFormSubmitted, labels, setAfterClose, setButtonKeys, setButtons, setSubmit]);

  return <div className="delete-route__dialog-text">{labels.deleteRouteDialogText}</div>;
};
